import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { DevicesList, Doctor, ProrithmNotification } from '../interfaces'
import { ProrithmWebSocketMessage } from '../features/Prorithm/websocketServices'

export interface IpdUpdateMessage {
  message: string
  patientLocation: string
  messageType: string
  patientID: string
}

interface PatientDetails {
  patientId: string
  patientName: string
  location: string
}

interface LocationList {
  id: string
  location: string
}

interface AppState {
  patient_data_loaded: boolean
  patient_name: string
  patient_uhid: string
  patient_dob: string
  patient_fhir_id: string
  patient_gender: string
  patient_bed_number: string
  patient_room_number: string
  patient_ward_name: string
  selectedPatientIndex: number | null
  selectedDeviceIndex: number | null
  searchQuery: string
  layout: string
  bedId: number | string
  bedNumber: string
  admissionDate: string
  masterPatientDetails: any[]
  currentLocation: string
  ipdUpdateMessageLocation: string
  isDeviceClicked: boolean
  locationsList: any[]
  onlyLocations: string[]
  doctorsList: Doctor[]
  thopNotificationMessages: any[]
  statusUpdateCount: number
  ipdRegistrationFlagCount: number
  ipdDataUpdateCount: number
  selectedDevice: DevicesList | null
  deviceList: DevicesList[]
  newIpdUpdateMessage: IpdUpdateMessage
  selectedCategory: string
  prorithmWebSocketConnectionStatus: boolean
  prorithmNotifications: any[]
  prorithmWebSocketMessage: any
  prorithmWebSocketCount: number
}

const initialState: AppState = {
  patient_data_loaded: false,
  patient_name: '',
  patient_uhid: '',
  patient_dob: '',
  patient_fhir_id: '',
  patient_gender: '',
  patient_bed_number: '',
  patient_room_number: '',
  patient_ward_name: '',
  selectedPatientIndex: null,
  selectedDeviceIndex: null,
  searchQuery: '',
  layout: 'none',
  bedId: '',
  bedNumber: '',
  admissionDate: '',
  masterPatientDetails: [],
  currentLocation: import.meta.env.VITE_AMALAPURAM_TEXT ?? '',
  ipdUpdateMessageLocation: '',
  isDeviceClicked: false,
  locationsList: [],
  onlyLocations: [],
  doctorsList: [],
  thopNotificationMessages: [],
  statusUpdateCount: 0,
  ipdRegistrationFlagCount: 0,
  ipdDataUpdateCount: 0,
  selectedDevice: null,
  deviceList: [],
  newIpdUpdateMessage: {
    message: '',
    patientLocation: '',
    messageType: '',
    patientID: '',
  },
  selectedCategory: import.meta.env.VITE_AMBULANCE_TEXT ?? '',
  prorithmWebSocketConnectionStatus: false,
  prorithmNotifications: [],
  prorithmWebSocketMessage: null,
  prorithmWebSocketCount: 0,
}

const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    setHasPatientDataLoaded(state, action: PayloadAction<boolean>) {
      state.patient_data_loaded = action.payload
    },
    setPatientName(state, action: PayloadAction<string>) {
      state.patient_name = action.payload
    },
    setPatientUHID(state, action: PayloadAction<string>) {
      state.patient_uhid = action.payload
    },
    setPatientDOB(state, action: PayloadAction<string>) {
      state.patient_dob = action.payload
    },
    setPatientGender(state, action: PayloadAction<string>) {
      state.patient_gender = action.payload
    },
    setPatientFhirId(state, action: PayloadAction<string>) {
      state.patient_fhir_id = action.payload
    },
    setPatientBedNumber(state, action: PayloadAction<string>) {
      state.patient_bed_number = action.payload
    },
    setPatientRoomNumber(state, action: PayloadAction<string>) {
      state.patient_room_number = action.payload
    },
    setPatientWardName(state, action: PayloadAction<string>) {
      state.patient_ward_name = action.payload
    },
    setSelectedPatientIndex(state, action: PayloadAction<number | null>) {
      state.selectedPatientIndex = action.payload
    },
    setSelectedDeviceIndex(state, action: PayloadAction<number | null>) {
      state.selectedDeviceIndex = action.payload
    },
    setLayout(state, action: PayloadAction<string>) {
      state.layout = action.payload
    },
    setBedId(state, action: PayloadAction<number | string>) {
      state.bedId = action.payload
    },
    setBedNumber(state, action: PayloadAction<string>) {
      state.bedNumber = action.payload
    },
    setAdmissionDate(state, action: PayloadAction<string>) {
      state.admissionDate = action.payload
    },
    setMasterPatientDetails(state, action: PayloadAction<PatientDetails[]>) {
      state.masterPatientDetails.push(action.payload)
    },
    setCurrentLocation(state, action: PayloadAction<string>) {
      state.currentLocation = action.payload
    },
    setIpdUpdateMessageLocation(state, action: PayloadAction<string>) {
      state.ipdUpdateMessageLocation = action.payload
    },
    setIsDeviceClicked(state, action: PayloadAction<boolean>) {
      state.isDeviceClicked = action.payload
    },
    setLocationsList(state, action: PayloadAction<LocationList[]>) {
      state.locationsList = action.payload
    },
    setOnlyLocations(state, action: PayloadAction<string[]>) {
      state.onlyLocations = action.payload
    },
    setSearchQuery(state, action: PayloadAction<string>) {
      state.searchQuery = action.payload
    },
    setDoctors(state, action: PayloadAction<Doctor[]>) {
      state.doctorsList = action.payload
    },
    addThopNotificationMessage(state, action: PayloadAction<any>) {
      state.thopNotificationMessages.push(action.payload)
    },
    deleteThopNotificationMessage(state, action: PayloadAction<number>) {
      state.thopNotificationMessages = state.thopNotificationMessages.filter(
        (_, index) => index !== action.payload
      )
    },
    setStatusUpdateCount(state) {
      state.statusUpdateCount += 1
    },
    setIpdUpdateCount(state) {
      state.ipdRegistrationFlagCount += 1
    },
    setIpdDataUpdateCount(state) {
      state.ipdDataUpdateCount += 1
    },
    setSelectedDevice(state, action: PayloadAction<DevicesList | null>) {
      state.selectedDevice = action.payload
    },
    setDeviceList(state, action: PayloadAction<DevicesList[]>) {
      state.deviceList = action.payload
    },
    setIpdUpdateMessage(state, action: PayloadAction<IpdUpdateMessage>) {
      state.newIpdUpdateMessage = action.payload
    },
    setSelectedCategory(state, action: PayloadAction<string>) {
      state.selectedCategory = action.payload
    },
    addProrithmNotification(
      state,
      action: PayloadAction<ProrithmNotification>
    ) {
      state.prorithmNotifications.push(action.payload)
    },
    removeProrithmNotification(state, action: PayloadAction<number>) {
      state.prorithmNotifications = state.prorithmNotifications.filter(
        (_, index) => index !== action.payload
      )
    },
    markProrithmNotificationAsRead(state, action: PayloadAction<number>) {
      const notification = state.prorithmNotifications[action.payload]
      if (notification) {
        notification.isRead = true
      }
    },
    setProrithmWebSocketMessage(
      state,
      action: PayloadAction<ProrithmWebSocketMessage>
    ) {
      state.prorithmWebSocketMessage = action.payload
    },
    setProrithmWebSocketCount(state) {
      state.prorithmWebSocketCount += 1
    },
    setProrithmWebSocketConnectionStatus(
      state,
      action: PayloadAction<boolean>
    ) {
      state.prorithmWebSocketConnectionStatus = action.payload
    },
  },
})

export const {
  setHasPatientDataLoaded,
  setPatientName,
  setPatientUHID,
  setPatientDOB,
  setPatientGender,
  setPatientFhirId,
  setPatientBedNumber,
  setPatientRoomNumber,
  setPatientWardName,
  setSelectedPatientIndex,
  setSelectedDeviceIndex,
  setLayout,
  setBedId,
  setBedNumber,
  setAdmissionDate,
  setMasterPatientDetails,
  setCurrentLocation,
  setIpdUpdateMessageLocation,
  setIsDeviceClicked,
  setLocationsList,
  setOnlyLocations,
  setSearchQuery,
  setDoctors,
  addThopNotificationMessage,
  deleteThopNotificationMessage,
  setStatusUpdateCount,
  setIpdUpdateCount,
  setIpdDataUpdateCount,
  setSelectedDevice,
  setDeviceList,
  setIpdUpdateMessage,
  setSelectedCategory,
  addProrithmNotification,
  removeProrithmNotification,
  markProrithmNotificationAsRead,
  setProrithmWebSocketMessage,
  setProrithmWebSocketCount,
  setProrithmWebSocketConnectionStatus,
} = appSlice.actions

export default appSlice.reducer
